/**
 * One source of truth for "can this device actually reach StockWise?".
 *
 * `navigator.onLine` alone is not the answer, and relying on it is why the
 * offline UI was impossible to see while testing locally: it reports whether
 * the machine has a network interface, not whether the API answers. Stopping
 * the backend (the normal way to simulate an outage on one machine) leaves it
 * `true`, so the app looked perfectly online while every request failed.
 *
 * This module therefore treats these as offline, in order of precedence:
 *  1. `simulated`  — a tester asked for it (`?offline=1`, or the dev chip).
 *     Requests fail deliberately so the real offline paths run.
 *  2. `browser`    — `navigator.onLine` is false: no network at all.
 *  3. `server`     — a request got no usable response. The device may be
 *     online, but StockWise cannot be reached; from the shop's point of view
 *     that is offline, and it is what a local test sees.
 *
 * Recovery is not assumed: while `server` is the reason, a lightweight probe
 * polls the API so the banner clears itself the moment the backend is back,
 * instead of waiting for a page reload.
 */

export type OfflineReason = 'browser' | 'server' | 'simulated'

export interface ConnectivityState {
  online: boolean
  /** Why we are offline. `null` while online. */
  reason: OfflineReason | null
}

const isBrowser = typeof window !== 'undefined'

/** Only ever set by the dev chip; the query param works in any build. */
const SIMULATE_STORAGE_KEY = 'stockwise:simulate-offline'
const PROBE_INTERVAL_MS = 5_000
const PROBE_TIMEOUT_MS = 4_000

/** Set by the API module, which is the only thing that knows the base URL.
 * The backend's liveness route sits beside the API prefix, not under it. */
let healthUrl = '/health';

let browserOffline = isBrowser ? navigator.onLine === false : false
let serverUnreachable = false
let simulated = readSimulated()
let started = false

let state: ConnectivityState = compute()

const listeners = new Set<(next: ConnectivityState) => void>()

function readSimulated(): boolean {
  if (!isBrowser) return false
  try {
    // Explicit query param: works in a production build too, so the installed
    // app can be tested without inventing a dev-only path.
    if (new URLSearchParams(window.location.search).get('offline') === '1') return true
    // Sticky, but only honoured in dev builds so a stray key can never leave a
    // real user stuck in fake offline mode.
    return import.meta.env.DEV && localStorage.getItem(SIMULATE_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function compute(): ConnectivityState {
  if (simulated) return { online: false, reason: 'simulated' }
  if (browserOffline) return { online: false, reason: 'browser' }
  if (serverUnreachable) return { online: false, reason: 'server' }
  return { online: true, reason: null }
}

function publish(): void {
  const next = compute()
  if (next.online === state.online && next.reason === state.reason) return
  state = next

  if (state.online) stopProbing()
  else if (state.reason === 'server') startProbing()

  for (const listener of listeners) listener(state)
}

export function getConnectivity(): ConnectivityState {
  return state
}

export function isOffline(): boolean {
  return !state.online
}

export function isSimulatedOffline(): boolean {
  return simulated
}

/** Subscribe to connectivity changes. Fires immediately with the current state. */
export function subscribeConnectivity(listener: (next: ConnectivityState) => void): () => void {
  listeners.add(listener)
  listener(state)
  return () => {
    listeners.delete(listener)
  }
}

/** Called once by the API module, which is where the base URL is known. */
export function setHealthUrl(url: string): void {
  healthUrl = url
}

/** A request produced a response the app can use. */
export function reportApiReachable(): void {
  if (!serverUnreachable) return
  serverUnreachable = false
  publish()
}

/** A request never got a response: DNS, refused, aborted, or no network. */
export function reportApiUnreachable(): void {
  if (serverUnreachable) return
  serverUnreachable = true
  publish()
}

/**
 * Ask the server directly whether it is back.
 *
 * Any status below 500 counts as reachable: a 401 or 404 still proves something
 * answered. A 5xx does not, because a dev proxy answers 500 when the backend
 * behind it is down — the exact case being tested locally.
 */
export async function probeNow(): Promise<boolean> {
  if (simulated || !isBrowser) return false

  let status = 0
  try {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS)
    try {
      const response = await fetch(healthUrl, {
        method: 'GET',
        cache: 'no-store',
        signal: controller.signal,
      })
      status = response.status
    } finally {
      clearTimeout(timer)
    }
  } catch {
    return false
  }

  if (status >= 500) return false
  reportApiReachable()
  return true
}

let probeTimer: number | null = null

function startProbing(): void {
  if (!isBrowser || probeTimer !== null) return
  probeTimer = window.setInterval(() => {
    void probeNow()
  }, PROBE_INTERVAL_MS)
}

function stopProbing(): void {
  if (probeTimer === null) return
  window.clearInterval(probeTimer)
  probeTimer = null
}

/** Turn fake-offline on or off. Only surfaced by the dev chip. */
export function setSimulatedOffline(on: boolean): void {
  simulated = on
  try {
    if (on) localStorage.setItem(SIMULATE_STORAGE_KEY, '1')
    else localStorage.removeItem(SIMULATE_STORAGE_KEY)
  } catch {
    // Private mode: the in-memory flag still works for this page.
  }
  publish()
}

/**
 * Turn simulation off completely: the sticky dev key and the `?offline=1`
 * query param, which `readSimulated` re-reads on every page load.
 *
 * Needed because sign-in goes through `fetchWithAuth`, which fails on purpose
 * while simulation is on — so being signed out with it enabled used to be a
 * one-way trap, with the only switch inside the authenticated layout.
 */
export function clearSimulatedOffline(): void {
  setSimulatedOffline(false)
  if (!isBrowser) return
  try {
    const url = new URL(window.location.href)
    if (!url.searchParams.has('offline')) return
    url.searchParams.delete('offline')
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)
  } catch {
    // Malformed URL: the in-memory flag is already off for this page.
  }
}

function handleBrowserOffline(): void {
  browserOffline = true
  publish()
}

function handleBrowserOnline(): void {
  browserOffline = false
  // The device has a network again, but the server may still be unreachable;
  // the probe resolves which. Don't assume online until it answers.
  if (serverUnreachable) void probeNow()
  else publish()
}

/**
 * Start tracking connectivity. Idempotent, because both the API module and the
 * banner want it and neither can assume the other ran first.
 */
export function startConnectivityWatch(): void {
  if (!isBrowser || started) return
  started = true
  browserOffline = navigator.onLine === false
  window.addEventListener('offline', handleBrowserOffline)
  window.addEventListener('online', handleBrowserOnline)
  if (serverUnreachable) startProbing()
}
